import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { sortProductLines } from 'pricing-pages-lib/utils/sortingUtils';
import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AllCombinedProductLines } from 'self-service-api/constants/AllCombinedProductLines';
import { buildFilteredHighlightedProducts, getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { useAppSelector } from '../../../reducers';
import { getAllFeaturesByAllProductLinesSelector } from '../../../selectors/FeatureSelectors';
import { bundledProductLinesForForApiNameSelector } from '../../../selectors/ProductConfigsSelectors';
import { isBundleListWithConsistentTypeSelector } from '../../../selectors/ProductsSelectors';
import { Section } from '../../common/SharedComponents';
import GenericBundleFeatureTableHeader from './GenericBundleFeatureTableHeader';
import { Table, TableContainer } from './SuiteFeatureTableComponents';
import { SuiteFeatureTableContent } from './SuiteFeatureTableContent';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const initialSectionState = AllCombinedProductLines.reduce((agg, curr) => {
  agg[curr] = true;
  return agg;
}, {});
const initialFeatureTableState = AllCombinedProductLines.reduce((agg, curr) => {
  agg[curr] = false;
  return agg;
}, {});
const GenericBundleFeatureTable = ({
  apiNames,
  trackingSource,
  screen,
  tab
}) => {
  const [isSectionExpanded, setIsSectionExpanded] = useState(initialSectionState);
  const [isFeatureTableExpanded, setIsFeatureTableExpanded] = useState(initialFeatureTableState);
  const features = useSelector(getAllFeaturesByAllProductLinesSelector);
  const featureTableRef = useRef(null);
  const productLinesFromBundle = useAppSelector(state => {
    return bundledProductLinesForForApiNameSelector(state, {
      apiName: apiNames[0]
    });
  });
  const isBundleWithConsistentType = useAppSelector(state => {
    return isBundleListWithConsistentTypeSelector(state, {
      apiNames
    });
  });
  if (!Object.keys(features).length || !productLinesFromBundle || !isBundleWithConsistentType) return null;
  const productLevelsFromBundle = new Set();
  apiNames.forEach(current => {
    const level = getProductLevelFromApiNameString(current);
    productLevelsFromBundle.add(level);
  });
  const productsByProductLine = {};
  for (const productLine of productLinesFromBundle) {
    const currentHighlightedProducts = buildFilteredHighlightedProducts({
      productLine,
      productLevels: [...productLevelsFromBundle]
    });
    productsByProductLine[productLine] = currentHighlightedProducts;
  }
  const handleExpandSectionChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isSectionExpanded[productLine] ? 'collapse' : 'expand'}-section`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab,
      trackingSource
    });
    setIsSectionExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };
  const handleExpandFeatureTableChange = productLine => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${isFeatureTableExpanded[productLine] ? 'collapse' : 'expand'}-table`,
      productLine,
      products: productsByProductLine[productLine],
      screen,
      tab,
      trackingSource
    });
    setIsFeatureTableExpanded(prev => {
      return Object.assign({}, prev, {
        [productLine]: !prev[productLine]
      });
    });
  };
  const sortedProductLines = sortProductLines(Object.keys(features));
  return /*#__PURE__*/_jsx(Section, {
    children: /*#__PURE__*/_jsxs(TableContainer, {
      ref: featureTableRef,
      columns: productLevelsFromBundle.size,
      "data-selenium": "bundle-feature-table",
      id: "feature-table",
      children: [/*#__PURE__*/_jsx(GenericBundleFeatureTableHeader, {
        levels: [...productLevelsFromBundle],
        apiNames: apiNames,
        screen: screen
      }), /*#__PURE__*/_jsx(Table, {
        children: sortedProductLines.map(productLine => productLinesFromBundle.includes(productLine) && /*#__PURE__*/_jsx(SuiteFeatureTableContent, {
          highlightedProducts: productsByProductLine[productLine],
          localFeatures: features[productLine],
          productLine: productLine,
          levels: productLevelsFromBundle,
          handleExpandSectionChange: handleExpandSectionChange,
          isSectionExpanded: isSectionExpanded,
          isFeatureTableExpanded: isFeatureTableExpanded,
          handleExpandFeatureTableChange: handleExpandFeatureTableChange
        }, productLine))
      })]
    })
  });
};
export default GenericBundleFeatureTable;